import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Scale, FileText, Users, Home as HomeIcon, Briefcase, Gavel, Shield, Building, FileSignature, Medal, Landmark, Globe, ClipboardCheck, Stamp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

// Unified service icons - same icons used on Services page
const ServiceIcon = ({ children }) => (
  <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center">
    {children}
  </div>
);

const HomePage = () => {
  const services = [
    {
      icon: <Globe className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Visa & Immigration',
      description: 'Complete visa services for expats, tourists, and long-term residents in Thailand.',
      link: '/services#visa'
    },
    {
      icon: <ClipboardCheck className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Work Permits',
      description: 'Professional assistance with work permit applications and renewals.',
      link: '/services#work-permits'
    },
    {
      icon: <Gavel className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Criminal Law',
      description: 'Expert defence representation for criminal cases with confidential handling.',
      link: '/criminal'
    },
    {
      icon: <Building className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Company Registration',
      description: 'Full business formation and company registration services in Thailand.',
      link: '/services#company'
    },
    {
      icon: <FileSignature className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Will and Testament',
      description: 'Secure your legacy with professionally drafted wills and estate planning.',
      link: '/services#will'
    },
    {
      icon: <HomeIcon className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Thai Property Law',
      description: 'Navigate property investment and ownership with expert legal guidance.',
      link: '/services#property'
    },
    {
      icon: <Users className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Divorce & Family Law',
      description: 'Compassionate support for divorce, custody, and family legal matters.',
      link: '/services#family'
    },
    {
      icon: <Scale className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Civil Law & Litigation',
      description: 'Professional representation for civil disputes and litigation cases.',
      link: '/services#civil'
    },
    {
      icon: <Stamp className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Notarial Services',
      description: 'Certified notarial services and attorney powers of attorney.',
      link: '/services#notarial'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Pattaya Solicitors | English Speaking Lawyers in Pattaya Thailand"
        description="Pattaya Solicitors - Leading law firm for expats in Pattaya Thailand. Immigration lawyer, property lawyer for foreign buyers, company registration, divorce attorney. English speaking solicitors. Call +66 38 412-415."
        keywords="Pattaya Solicitors, Solicitors in Pattaya Thailand, Pattaya Lawyers Thailand, English Speaking Lawyer Pattaya, Expat Solicitor Pattaya, Law Firm Pattaya for Foreigners, Immigration Lawyer Pattaya Thailand, Property Lawyer Pattaya, Company Registration Lawyer Pattaya, Divorce Attorney Pattaya"
        path="/"
        schemaType="home"
      />
      {/* Hero Section */}
      <section className="relative bg-[#0F1A2F] text-white overflow-hidden">
        
        <div className="container mx-auto px-6 py-24 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <div className="bg-[#F5E7D2] text-[#000A22] px-5 py-2.5 rounded-full font-medium text-sm flex items-center gap-2">
                  <Scale className="w-4 h-4 text-[#D4A74A]" />
                  Trusted Legal Experts
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Pattaya Lawyers & Solicitors for Expats
                <span className="block text-[#D4A74A] mt-2">English Speaking Law Firm in Pattaya Thailand</span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Expert legal representation for foreigners in Thailand. English-speaking attorneys with 20+ years experience helping expats navigate Thai law.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                  asChild
                >
                  <a href="tel:+6638412415">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </a>
                </Button>
                
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300"
                  asChild
                >
                  <Link to="/contact">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat With Us
                  </Link>
                </Button>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 text-sm pt-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#D4A74A]" />
                  <a href="tel:+6638412415" className="hover:text-[#D4A74A] transition-colors">+66 (38) 412-415</a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#D4A74A]" />
                  <a href="tel:+66818383869" className="hover:text-[#D4A74A] transition-colors">+66 (81) 838-3869</a>
                </div>
              </div>
            </div>
            
            <div className="relative pb-16 md:pb-0">
              {/* Main image container */}
              <div className="relative">
                {/* Professional headshot with premium shadow */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-4 ring-[#D4A74A]/30 hover:ring-[#D4A74A]/50 transition-all duration-500 hover:scale-105">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/qbndhwu7_chantima-attorney.jpg"
                    alt="English speaking lawyer Pattaya consultation"
                    className="w-full h-auto"
                  />
                  {/* Gradient overlay for depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000A22]/40 via-transparent to-transparent"></div>
                </div>
                
                {/* Credentials badge - positioned completely below image on mobile, overlapping on desktop */}
                <div className="absolute -bottom-8 md:-bottom-6 left-1/2 transform -translate-x-1/2 bg-[#D4A74A] text-[#000A22] px-6 md:px-8 py-3 md:py-4 rounded-2xl shadow-xl whitespace-nowrap z-10">
                  <div className="flex items-center gap-2 md:gap-3">
                    <Scale className="w-5 h-5 md:w-6 md:h-6" />
                    <div>
                      <div className="text-xs md:text-sm font-medium">Licensed Attorney</div>
                      <div className="text-[10px] md:text-xs opacity-90">20+ Years Experience</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white py-10 border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Licensed with Lawyer's Council - Medal/Certification */}
            <div className="flex flex-col items-center text-center space-y-3 p-4 hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-20 h-20 bg-[#F5E7D2] rounded-full flex items-center justify-center">
                <Medal className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />
              </div>
              <p className="text-sm font-bold text-[#000A22]">Licensed with Lawyer's Council</p>
              <p className="text-xs text-gray-600">Fully registered Thai law firm</p>
            </div>
            
            {/* 20+ Years Experience - Large text, no icon */}
            <div className="flex flex-col items-center text-center space-y-3 p-4 hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-20 h-20 bg-[#F5E7D2] rounded-full flex items-center justify-center">
                <span className="text-3xl font-bold text-[#D4A74A]">20+</span>
              </div>
              <p className="text-sm font-bold text-[#000A22]">20+ Years Experience</p>
              <p className="text-xs text-gray-600">Serving expats in Pattaya</p>
            </div>
            
            {/* Embassy Recommended - Courthouse/Building */}
            <div className="flex flex-col items-center text-center space-y-3 p-4 hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-20 h-20 bg-[#F5E7D2] rounded-full flex items-center justify-center">
                <Landmark className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} />
              </div>
              <p className="text-sm font-bold text-[#000A22]">Embassy Recommended</p>
              <p className="text-xs text-gray-600">Trusted by foreign embassies</p>
            </div>
            
            {/* English-Speaking Law Firm - Speech bubble with EN */}
            <div className="flex flex-col items-center text-center space-y-3 p-4 hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-20 h-20 bg-[#F5E7D2] rounded-full flex items-center justify-center relative">
                <MessageCircle className="w-10 h-10 text-[#D4A74A]" strokeWidth={2} fill="none" />
                <span className="absolute text-sm font-bold text-[#D4A74A]">EN</span>
              </div>
              <p className="text-sm font-bold text-[#000A22]">English-Speaking Law Firm</p>
              <p className="text-xs text-gray-600">Full expat legal support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section with Office Images */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold text-[#000A22]">
                  Your Trusted Legal Partner in Pattaya
                  <span className="block text-2xl text-[#D4A74A] mt-2">Specialising in Expat Legal Services</span>
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
                  <p>
                    <strong>Pattaya Solicitors (DMC Inter Law & Consultants)</strong> is Pattaya's premier law firm offering comprehensive legal services tailored for foreign residents and expats living in Thailand.
                  </p>
                  <p>
                    Whether you're buying property, starting a business, facing visa issues, or need urgent legal representation, our English-speaking attorneys provide professional guidance through every step of the Thai legal system.
                  </p>
                  <p className="text-[#D4A74A] font-semibold">
                    ✓ Licensed with the Lawyer's Council of Thailand<br />
                    ✓ Recommended by International Embassies<br />
                    ✓ 20+ Years Serving the Expat Community
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/sz6rc02d_office-staff.jpg"
                    alt="Legal team Pattaya solicitors courtroom"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 mt-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_d7574d0a-c2db-4bd5-bcf9-40578eb82c27/artifacts/tc5i5lng_pattaya%20solicitors%20main%20office%20desk%20front.jpg"
                    alt="Pattaya solicitors main office reception desk"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Background accents */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4A74A] opacity-5 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4A74A] opacity-5 blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#F5E7D2] text-[#D4A74A] px-6 py-2 rounded-full font-semibold text-sm mb-4">
              COMPREHENSIVE LEGAL SERVICES
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#000A22] mb-4">
              How We Help Expats in Pattaya
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional legal services designed specifically for foreign residents navigating Thai law
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index}
                className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-[#D4A74A] cursor-pointer bg-white"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#000A22] mb-3 group-hover:text-[#D4A74A] transition-colors">{service.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="text-[#D4A74A] font-semibold hover:underline inline-flex items-center gap-2"
                  >
                    Learn more →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#000A22] via-[#001438] to-[#000A22] text-white relative overflow-hidden">
        {/* Animated elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-[#D4A74A] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#D4A74A] rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="inline-block bg-red-600/20 border border-red-500 text-red-300 px-6 py-2 rounded-full font-semibold text-sm mb-4 animate-pulse">
              ⚡ URGENT LEGAL MATTER? WE'RE HERE 24/7
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold">Get Expert Legal Help Today</h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              Don't navigate Thai law alone. Our English-speaking attorneys are ready to help. Click the chat bubble below or call us immediately for a confidential consultation.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 pt-4">
              <Button 
                size="lg"
                className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-bold px-10 py-7 text-xl transition-all duration-300 hover:shadow-2xl hover:scale-110 shadow-xl"
                asChild
              >
                <a href="tel:+6638412415" className="flex items-center gap-3">
                  <Phone className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-xs opacity-80">CALL NOW</div>
                    <div>+66 (38) 412-415</div>
                  </div>
                </a>
              </Button>
              
              <Button 
                size="lg"
                className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-bold px-10 py-7 text-xl transition-all duration-300 hover:shadow-2xl hover:scale-110 shadow-xl"
                asChild
              >
                <a href="tel:+66818383869" className="flex items-center gap-3">
                  <Phone className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-xs opacity-80">MOBILE</div>
                    <div>+66 (81) 838-3869</div>
                  </div>
                </a>
              </Button>
            </div>
            
            <p className="text-sm text-gray-400 pt-4">
              All consultations are strictly confidential • Licensed attorneys • English spoken
            </p>
          </div>
        </div>
      </section>

      {/* Contact Snapshot */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-[#000A22] mb-4">Office Address</h3>
                <p className="text-gray-700 leading-relaxed">
                  20/173, Moo 10, South Pattaya Road,<br />
                  Pattaya City, Nongprue, Banglamung,<br />
                  Chonburi 20150
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-[#000A22] mb-4">Contact Information</h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Telephone:</strong> +66 (38) 412-415, +66 (81) 838-3869</p>
                  <p><strong>Email:</strong> <a href="mailto:info@pattayasolicitors.com" className="text-[#D4A74A] hover:underline">info@pattayasolicitors.com</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
